import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";
import { formatRecordingTime } from "../utils/recording-time";

const WAVEFORM_BARS = 120;
const MIN_WAVEFORM_HEIGHT = 0.16;

export type RecordingWaveformBar = { at: number; height: number };

const LOADING_WAVEFORM_BARS: RecordingWaveformBar[] = Array.from({ length: WAVEFORM_BARS }, (_, index) => ({
  at: index / WAVEFORM_BARS,
  height: MIN_WAVEFORM_HEIGHT,
}));

export async function readRecordingWaveform(blob: Blob): Promise<RecordingWaveformBar[]> {
  const context = new AudioContext();
  try {
    const buffer = await context.decodeAudioData(await blob.arrayBuffer());
    const samples = buffer.getChannelData(0);
    const peaks = Array.from({ length: WAVEFORM_BARS }, (_, index) => {
      const start = Math.floor((index * samples.length) / WAVEFORM_BARS);
      const end = Math.floor(((index + 1) * samples.length) / WAVEFORM_BARS);
      const step = Math.max(1, Math.floor((end - start) / 64));
      let peak = 0;
      for (let sample = start; sample < end; sample += step) {
        peak = Math.max(peak, Math.abs(samples[sample] ?? 0));
      }
      return peak;
    });
    const maximum = Math.max(...peaks, 0.01);
    return peaks.map((peak, index) => ({ at: index / WAVEFORM_BARS, height: Math.max(MIN_WAVEFORM_HEIGHT, Math.sqrt(peak / maximum)) }));
  } finally {
    await context.close();
  }
}

export type RecordWaveProgressBarProps = {
  canSeek: boolean;
  currentTime: number;
  loading?: boolean;
  onSeek: (position: number) => void;
  total: number;
  waveform: readonly RecordingWaveformBar[];
};

export function RecordWaveProgressBar({ canSeek, currentTime, loading = false, onSeek, total, waveform }: RecordWaveProgressBarProps) {
  const { t } = useI18n();
  const progress = total > 0 ? Math.min(1, Math.max(0, currentTime / total)) : 0;
  const visibleBars = loading ? LOADING_WAVEFORM_BARS : waveform;

  return (
    <div aria-busy={loading} className="mt-3 flex items-center gap-3">
      <div className="relative min-w-0 flex-1 rounded-sm">
        {loading ? (
          <span className="sr-only" role="status">
            {t("tickets.recording.loading")}
          </span>
        ) : null}
        {visibleBars.length > 0 ? (
          <div
            aria-hidden="true"
            className={cn("relative h-(--bar-height)", loading && "animate-pulse")}
            style={{ "--bar-height": "calc(var(--spacing) * 6)" } as React.CSSProperties}
          >
            <div className="flex h-full items-center gap-0.5">
              {visibleBars.map((bar) => (
                <span
                  key={bar.at}
                  className="min-w-0 flex-1 rounded-sm bg-border transition-all duration-500 ease-out motion-reduce:transition-none max-sm:odd:hidden"
                  style={{ height: `${Math.round(bar.height * 100)}%` }}
                />
              ))}
            </div>
            <div
              className="absolute inset-0 transition-all duration-300 ease-linear motion-reduce:transition-none"
              style={{ clipPath: `inset(0 ${loading ? 100 : 100 - progress * 100}% 0 0)` }}
            >
              <div className="flex h-(--bar-height) items-center gap-0.5">
                {visibleBars.map((bar) => (
                  <span
                    key={bar.at}
                    className="min-w-0 flex-1 rounded-sm bg-primary transition-all duration-500 ease-out motion-reduce:transition-none max-sm:odd:hidden"
                    style={{ height: `${Math.round(bar.height * 100)}%` }}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div aria-hidden="true" className="flex h-8 items-center">
            <span className="h-2 w-full rounded-full bg-muted">
              <span className="block h-full rounded-full bg-primary" style={{ width: `${progress * 100}%` }} />
            </span>
          </div>
        )}
        {!loading ? (
          <input
            aria-label={t("tickets.recording.seek")}
            aria-valuetext={`${formatRecordingTime(currentTime)} / ${formatRecordingTime(total)}`}
            className="absolute inset-0 size-full cursor-pointer opacity-0 disabled:cursor-default"
            disabled={!canSeek}
            max={total || 1}
            min={0}
            step={0.1}
            type="range"
            value={Math.min(currentTime, total)}
            onChange={(event) => onSeek(Number(event.target.value))}
          />
        ) : null}
      </div>
      <output className="shrink-0 text-xs tabular-nums text-muted-foreground">
        {formatRecordingTime(currentTime)} / {formatRecordingTime(total)}
      </output>
    </div>
  );
}
